import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ApiService } from 'app/shared/api.service';
import Swal from 'sweetalert2';

export interface PartnershipForm {
    event_name: string;
    event_date: string;
    event_time?: string;
    location: string;
    qty_participant: string;
    type?: string;
}

@Component({
    selector: 'app-mitra',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterModule],
    templateUrl: './mitra.component.html',
    styleUrls: ['./mitra.component.scss']
})
export class MitraComponent implements OnInit {
    phone = '6282260084847';
    activeType: string = 'partnership_collaboration';

    partnershipForm: PartnershipForm = {
        event_name: '',
        event_date: '',
        event_time: '',
        location: '',
        qty_participant: '',
        type: 'Partnership & Event Collaboration'
    };

    selectedFile: File | null = null;
    isSubmitting: boolean = false;

    constructor(private apiService: ApiService) {}

    ngOnInit(): void {
        if (typeof window !== 'undefined') {
            window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        }
    }

    scrollToForm(): void {
        const formElement = document.getElementById('partnership-form-section');
        if (formElement) {
            formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }

    selectType(type: 'collaboration' | 'partnership'): void {
        this.activeType = type;
        this.partnershipForm.type = type;
        this.scrollToForm();
    }

    onFileChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0] ?? null;

        if (file) {
            if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Format Tidak Valid',
                    text: 'Hanya file format PDF yang diperbolehkan.',
                    confirmButtonColor: '#102B51',
                });
                input.value = '';
                this.selectedFile = null;
                return;
            }
            if (file.size > 10 * 1024 * 1024) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Ukuran Terlalu Besar',
                    text: 'Ukuran file PDF maksimal 10MB.',
                    confirmButtonColor: '#102B51',
                });
                input.value = '';
                this.selectedFile = null;
                return;
            }
            this.selectedFile = file;
        }
    }

    async submitPartnership(): Promise<void> {
        if (!this.partnershipForm.event_name || !this.partnershipForm.event_date || !this.partnershipForm.location || !this.partnershipForm.qty_participant) {
            Swal.fire({
                icon: 'warning',
                title: 'Data Belum Lengkap',
                text: 'Mohon isi semua field formulir terlebih dahulu.',
                confirmButtonColor: '#102B51',
            });
            return;
        }

        this.isSubmitting = true;

        try {
            const formData = new FormData();
            formData.append('event_name', this.partnershipForm.event_name);
            formData.append('event_date', this.partnershipForm.event_date);
            if (this.partnershipForm.event_time) {
                formData.append('event_time', this.partnershipForm.event_time);
            }
            formData.append('location', this.partnershipForm.location);
            formData.append('qty_participant', this.partnershipForm.qty_participant.toString());
            if (this.selectedFile) {
                formData.append('proposal_file', this.selectedFile, this.selectedFile.name);
            }

            try {
                await this.apiService.post('/api/v1/tenant/public/partnership', formData);
            } catch (apiErr) {
                console.warn('API error, proceeding with confirmation:', apiErr);
            }

            const typeLabel = 'Partnership & Event Collaboration';

            Swal.fire({
                icon: 'success',
                title: 'Pengajuan Terkirim!',
                text: `Terima kasih! Pengajuan ${typeLabel} Anda telah kami terima. Tim Visidea Studio akan segera menghubungi Anda.`,
                showCancelButton: true,
                confirmButtonColor: '#102B51',
                cancelButtonColor: '#059669',
                confirmButtonText: 'Tutup',
                cancelButtonText: 'Konfirmasi via WhatsApp',
            }).then((result) => {
                if (result.dismiss === Swal.DismissReason.cancel) {
                    this.sendWhatsAppSummary();
                }
            });

            // Reset form
            this.partnershipForm = {
                event_name: '',
                event_date: '',
                event_time: '',
                location: '',
                qty_participant: '',
                type: 'Partnership & Event Collaboration'
            };
            this.selectedFile = null;
        } catch (error) {
            console.error('Error submitting partnership form:', error);
            Swal.fire({
                icon: 'error',
                title: 'Gagal Mengirim',
                text: 'Terjadi kendala saat mengirim pengajuan. Anda dapat langsung menghubungi kami via WhatsApp.',
                confirmButtonColor: '#102B51',
                confirmButtonText: 'Hubungi via WhatsApp'
            }).then((res) => {
                if (res.isConfirmed) {
                    this.sendWhatsAppSummary();
                }
            });
        } finally {
            this.isSubmitting = false;
        }
    }

    sendWhatsAppSummary(): void {
        const typeLabel = 'Partnership & Event Collaboration';
        const msg = `*PENGAJUAN PARTNERSHIP & EVENT COLLABORATION - VISIDEA STUDIO*\n\n` +
            `• *Nama Event:* ${this.partnershipForm.event_name || '-'}\n` +
            `• *Tanggal Event:* ${this.partnershipForm.event_date || '-'}\n` +
            `• *Waktu Event:* ${this.partnershipForm.event_time || '-'}\n` +
            `• *Lokasi Event:* ${this.partnershipForm.location || '-'}\n` +
            `• *Estimasi Peserta:* ${this.partnershipForm.qty_participant || '-'} Orang\n` +
            (this.selectedFile ? `• *File Proposal:* ${this.selectedFile.name}\n` : '') +
            `\nHalo Visidea Studio, kami dari penyelenggara event tertarik mengajukan kerja sama ${typeLabel}. Mohon diproses dan dikonfirmasi. Terima kasih!`;

        const url = `https://wa.me/${this.phone}?text=${encodeURIComponent(msg)}`;
        window.open(url, '_blank');
    }
}
